import React, { forwardRef } from 'react';
import { useTheme, alpha } from '@mui/material/styles';
import MaterialTable from '@material-table/core';
import { Paper, Box, Stack, Typography, Button } from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import AddBox from '@mui/icons-material/AddBox';
import ArrowDownward from '@mui/icons-material/ArrowDownward';
import Check from '@mui/icons-material/Check';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import Clear from '@mui/icons-material/Clear';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import Edit from '@mui/icons-material/Edit';
import FilterList from '@mui/icons-material/FilterList';
import FirstPage from '@mui/icons-material/FirstPage';
import LastPage from '@mui/icons-material/LastPage';
import Remove from '@mui/icons-material/Remove';
import SaveAlt from '@mui/icons-material/SaveAlt';
import Search from '@mui/icons-material/Search';
import ViewColumn from '@mui/icons-material/ViewColumn';

const tableIcons = {
  Add: forwardRef((props, ref) => <AddBox {...props} ref={ref} />),
  Check: forwardRef((props, ref) => <Check {...props} ref={ref} />),
  Clear: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
  Delete: forwardRef((props, ref) => <DeleteOutline {...props} ref={ref} />),
  DetailPanel: forwardRef((props, ref) => <ChevronRight {...props} ref={ref} />),
  Edit: forwardRef((props, ref) => <Edit {...props} ref={ref} />),
  Export: forwardRef((props, ref) => <SaveAlt {...props} ref={ref} />),
  Filter: forwardRef((props, ref) => <FilterList {...props} ref={ref} />),
  FirstPage: forwardRef((props, ref) => <FirstPage {...props} ref={ref} />),
  LastPage: forwardRef((props, ref) => <LastPage {...props} ref={ref} />),
  NextPage: forwardRef((props, ref) => <ChevronRight {...props} ref={ref} />),
  PreviousPage: forwardRef((props, ref) => <ChevronLeft {...props} ref={ref} />),
  ResetSearch: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
  Search: forwardRef((props, ref) => <Search {...props} ref={ref} />),
  SortArrow: forwardRef((props, ref) => <ArrowDownward {...props} ref={ref} />),
  ThirdStateCheck: forwardRef((props, ref) => <Remove {...props} ref={ref} />),
  ViewColumn: forwardRef((props, ref) => <ViewColumn {...props} ref={ref} />),
};

/**
 * Modern styled Material Table component
 * Provides consistent styling across all tables in the application
 */
export default function ModernTable({ title, ...props }) {
  const theme = useTheme();

  const modernTableStyle = {
    // Container styles
    container: {
      borderRadius: 4,
      overflow: 'hidden',
      boxShadow: `0 8px 20px ${alpha(theme.palette.primary.main, 0.1)}`,
      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    },

    // Header styles
    header: {
      backgroundColor:
        theme.palette.mode === 'dark'
          ? alpha(theme.palette.primary.main, 0.15)
          : alpha(theme.palette.primary.main, 0.08),
      '& .MuiToolbar-root': {
        padding: '24px 32px',
        minHeight: 'auto',
      },
      '& .MuiTypography-h6': {
        fontWeight: 700,
        fontSize: '1.25rem',
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
        backgroundClip: 'text',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
      },
    },

    // Table header row
    tableHeader: {
      backgroundColor:
        theme.palette.mode === 'dark'
          ? alpha(theme.palette.background.paper, 0.6)
          : alpha(theme.palette.grey[50], 0.8),
      '& .MuiTableCell-head': {
        fontWeight: 700,
        fontSize: '0.875rem',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        color: theme.palette.text.primary,
        borderBottom: `2px solid ${theme.palette.primary.main}`,
        padding: '20px 24px',
        whiteSpace: 'nowrap',
      },
    },

    // Table body
    tableBody: {
      '& .MuiTableRow-root': {
        transition: 'all 0.2s ease',
        '&:hover': {
          backgroundColor: alpha(theme.palette.primary.main, 0.05),
          transform: 'scale(1.001)',
          boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.1)}`,
        },
        '&:nth-of-type(even)': {
          backgroundColor: alpha(theme.palette.action.hover, 0.02),
        },
      },
      '& .MuiTableCell-body': {
        fontSize: '0.875rem',
        padding: '18px 24px',
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
      },
    },

    // Pagination
    pagination: {
      backgroundColor:
        theme.palette.mode === 'dark'
          ? alpha(theme.palette.background.paper, 0.4)
          : alpha(theme.palette.grey[50], 0.5),
      borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
      '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
        fontWeight: 500,
        fontSize: '0.875rem',
      },
    },

    // Search field
    searchField: {
      '& .MuiInputBase-root': {
        borderRadius: '25px',
        backgroundColor: alpha(theme.palette.background.paper, 0.8),
        border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
        transition: 'all 0.3s ease',
        paddingLeft: '8px',
        '&:hover': {
          borderColor: alpha(theme.palette.primary.main, 0.5),
          backgroundColor: theme.palette.background.paper,
          boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`,
          transform: 'translateY(-1px)',
        },
        '&.Mui-focused': {
          borderColor: theme.palette.primary.main,
          backgroundColor: theme.palette.background.paper,
          boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.25)}`,
          transform: 'translateY(-1px)',
        },
        '& fieldset': {
          border: 'none',
        },
      },
      '& .MuiInputBase-input': {
        padding: '10px 16px',
        fontSize: '0.875rem',
        fontWeight: 500,
      },
      '& .MuiInputAdornment-root': {
        marginLeft: '8px',
      },
    },

    // Actions
    actions: {
      '& .MuiIconButton-root': {
        borderRadius: 2,
        padding: '8px',
        transition: 'all 0.2s ease',
        '&:hover': {
          backgroundColor: alpha(theme.palette.primary.main, 0.1),
          color: theme.palette.primary.main,
          transform: 'scale(1.1)',
        },
      },
    },
  };

  return (
    <Box
      sx={{
        width: '100%',
        borderRadius: 3.2,
        overflow: 'hidden',
        boxShadow: `0 6.4px 16px ${alpha(theme.palette.primary.main, 0.1)}`,
        border: `0.8px solid ${alpha(theme.palette.divider, 0.1)}`,

        // Header toolbar styles (0.8x)
        '& .MuiToolbar-root:not(.MuiTablePagination-toolbar)': {
          background: `linear-gradient(135deg, ${alpha(
            theme.palette.primary.main,
            0.08
          )} 0%, ${alpha(theme.palette.secondary.main, 0.08)} 100%) !important`,
          borderRadius: '3.2px 3.2px 0 0 !important',
          padding: '9.6px 25.6px !important',
          minHeight: 'auto !important',
        },

        // Title styles (0.8x)
        '& .MuiToolbar-root .MuiTypography-h6': {
          fontWeight: '700 !important',
          fontSize: '1rem !important',
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
        },

        // Table header row (0.8x)
        '& .MuiTableHead-root': {
          backgroundColor:
            theme.palette.mode === 'dark'
              ? alpha(theme.palette.background.paper, 0.6)
              : alpha(theme.palette.grey[50], 0.8),
        },
        '& .MuiTableCell-head': {
          fontWeight: '700 !important',
          fontSize: '0.7rem !important',
          textTransform: 'uppercase',
          letterSpacing: '0.4px',
          color: `${theme.palette.text.primary} !important`,
          borderBottom: `1.6px solid ${alpha(theme.palette.divider, 0.2)} !important`,
          padding: '8px 12px !important',
          whiteSpace: 'nowrap',
        },

        // Table body (0.8x)
        '& .MuiTableBody-root .MuiTableRow-root': {
          transition: 'background-color 0.2s ease !important',
          '&:hover': {
            backgroundColor: `${alpha(theme.palette.primary.main, 0.05)} !important`,
          },
          '&:nth-of-type(even)': {
            backgroundColor: `${alpha(theme.palette.action.hover, 0.02)} !important`,
          },
        },
        '& .MuiTableCell-body': {
          fontSize: '0.7rem !important',
          padding: '8px 12px !important',
          borderBottom: `0.8px solid ${alpha(theme.palette.divider, 0.08)} !important`,
        },
        // Detail panel arrow column - make it compact (only for main table, not nested tables)
        '& > table > thead > tr > th:first-of-type, & > table > tbody > tr > td:first-of-type': {
          width: '36px !important',
          minWidth: '36px !important',
          maxWidth: '36px !important',
          padding: '4px 8px !important',
        },

        // Pagination (0.8x)
        '& .MuiTablePagination-root': {
          backgroundColor:
            theme.palette.mode === 'dark'
              ? alpha(theme.palette.background.paper, 0.4)
              : alpha(theme.palette.grey[50], 0.5),
          borderTop: `0.8px solid ${alpha(theme.palette.divider, 0.1)}`,
        },
        '& .MuiTablePagination-toolbar': {
          padding: '12.8px 19.2px !important',
          minHeight: 'auto !important',
        },
        '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
          fontWeight: '500 !important',
          fontSize: '0.7rem !important',
          margin: '0 !important',
          marginBottom: '0 !important',
        },
        '& .MuiTablePagination-select': {
          paddingTop: '0 !important',
          paddingBottom: '0 !important',
        },
        // Search field styles (0.8x)
        '& .MuiToolbar-root .MuiTextField-root': {
          '& .MuiInputBase-root': {
            borderRadius: '20px !important',
            backgroundColor: `${alpha(theme.palette.background.paper, 0.8)} !important`,
            border: `1.6px solid ${alpha(theme.palette.primary.main, 0.2)} !important`,
            transition: 'all 0.3s ease !important',
            paddingLeft: '6.4px !important',
            '&:hover': {
              borderColor: `${alpha(theme.palette.primary.main, 0.5)} !important`,
              backgroundColor: `${theme.palette.background.paper} !important`,
              boxShadow: `0 3.2px 9.6px ${alpha(theme.palette.primary.main, 0.15)} !important`,
              transform: 'translateY(-0.8px) !important',
            },
            '&.Mui-focused': {
              borderColor: `${theme.palette.primary.main} !important`,
              backgroundColor: `${theme.palette.background.paper} !important`,
              boxShadow: `0 3.2px 16px ${alpha(theme.palette.primary.main, 0.25)} !important`,
              transform: 'translateY(-0.8px) !important',
            },
            '& fieldset': {
              border: 'none !important',
            },
          },
          '& .MuiInputBase-input': {
            padding: '8px 12.8px !important',
            fontSize: '0.7rem !important',
            fontWeight: '500 !important',
          },
          '& .MuiInputAdornment-root': {
            marginLeft: '6.4px !important',
          },
          '& .MuiInput-underline:before, & .MuiInput-underline:after, & .MuiInput-underline:hover:not(.Mui-disabled):before':
            {
              borderBottom: 'none !important',
            },
        },

        // Action buttons (0.8x)
        '& .MuiIconButton-root': {
          borderRadius: '6.4px !important',
          padding: '6.4px !important',
          transition: 'all 0.2s ease !important',
          '&:hover': {
            backgroundColor: `${alpha(theme.palette.primary.main, 0.1)} !important`,
            color: `${theme.palette.primary.main} !important`,
            transform: 'scale(1.1) !important',
          },
        },
      }}
    >
      <MaterialTable
        title={title}
        icons={tableIcons}
        {...props}
        options={{
          // Default modern options
          pageSize: 10,
          pageSizeOptions: [5, 10, 20, 50, 100],
          search: true,
          searchFieldAlignment: 'right',
          searchFieldStyle: {
            minWidth: '300px',
            borderRadius: '25px',
          },
          sorting: true,
          filtering: false,
          exportButton: true,
          exportAllData: true,
          actionsColumnIndex: -1,
          draggable: false,
          ...props.options,
        }}
        components={{
          Container: (props) => <Paper {...props} elevation={0} sx={{ boxShadow: 'none' }} />,
          ...props.components,
        }}
        localization={{
          pagination: {
            labelDisplayedRows: '{from}-{to} of {count}',
            labelRowsSelect: 'rows',
            firstAriaLabel: 'First Page',
            firstTooltip: 'First Page',
            previousAriaLabel: 'Previous Page',
            previousTooltip: 'Previous Page',
            nextAriaLabel: 'Next Page',
            nextTooltip: 'Next Page',
            lastAriaLabel: 'Last Page',
            lastTooltip: 'Last Page',
          },
          toolbar: {
            searchTooltip: 'Search',
            searchPlaceholder: 'Search...',
            exportTitle: 'Export',
            exportAriaLabel: 'Export',
            exportName: 'Export as CSV',
          },
          header: {
            actions: 'Actions',
          },
          body: {
            emptyDataSourceMessage: 'No records to display',
            filterRow: {
              filterTooltip: 'Filter',
            },
          },
          ...props.localization,
        }}
      />
    </Box>
  );
}

/**
 * Modern page container component
 * Wraps pages with consistent styling
 */
export const ModernPageContainer = ({ children, maxWidth = '100%' }) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth,
        mx: 'auto',
        pt: { xs: 1, sm: 1.5, md: 2 },
        px: { xs: 2, sm: 3, md: 4 },
        pb: { xs: 2, sm: 3, md: 4 },
        minHeight: 'calc(100vh - 100px)',
      }}
    >
      {children}
    </Box>
  );
};

/**
 * Modern page header component with Paper background
 * Supports multiple buttons through the `buttons` prop
 *
 * @param {string} title - Header title
 * @param {string} subtitle - Optional subtitle
 * @param {Component} icon - Optional icon component
 * @param {Array} buttons - Array of button objects: [{ text, onClick, icon, variant, color }]
 * @param {string} buttonText - (Legacy) Single button text
 * @param {Function} onButtonClick - (Legacy) Single button click handler
 * @param {Component} buttonIcon - (Legacy) Single button icon
 * @param {string} secondaryButtonText - (Legacy) Secondary button text
 * @param {Function} onSecondaryButtonClick - (Legacy) Secondary button click handler
 * @param {Component} secondaryButtonIcon - (Legacy) Secondary button icon
 */
export const ModernPageHeader = ({
  title,
  subtitle,
  icon: Icon,
  buttons = [],
  buttonText,
  onButtonClick,
  buttonIcon: ButtonIcon,
  secondaryButtonText,
  onSecondaryButtonClick,
  secondaryButtonIcon: SecondaryButtonIcon,
}) => {
  const theme = useTheme();

  // Convert legacy button props to buttons array for backward compatibility
  const legacyButtons = [];
  if (buttonText && onButtonClick) {
    legacyButtons.push({
      text: buttonText,
      onClick: onButtonClick,
      icon: ButtonIcon,
      variant: 'contained',
    });
  }
  if (secondaryButtonText && onSecondaryButtonClick) {
    legacyButtons.push({
      text: secondaryButtonText,
      onClick: onSecondaryButtonClick,
      icon: SecondaryButtonIcon,
      variant: 'outlined',
    });
  }

  // Use provided buttons array or fall back to legacy buttons
  const displayButtons = buttons.length > 0 ? buttons : legacyButtons;

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2.4, sm: 3.2, md: 4 },
        mb: 3.2,
        borderRadius: 3.2,
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(
          theme.palette.secondary.main,
          0.08
        )} 100%)`,
        border: `0.8px solid ${alpha(theme.palette.divider, 0.1)}`,
        borderBottom: `2.4px solid ${theme.palette.primary.main}`,
      }}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        alignItems={{ xs: 'flex-start', md: 'center' }}
        justifyContent="space-between"
        spacing={2.4}
      >
        <Stack direction="row" alignItems="center" spacing={1.6}>
          {Icon && (
            <Box
              sx={{
                width: 38.4,
                height: 38.4,
                borderRadius: 2.4,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: `0 3.2px 16px ${alpha(theme.palette.primary.main, 0.3)}`,
              }}
            >
              <Icon sx={{ color: 'white', fontSize: 22.4 }} />
            </Box>
          )}
          <Box>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                fontSize: '1.6rem',
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: subtitle ? 0.4 : 0,
              }}
            >
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, fontSize: '0.7rem' }}>
                {subtitle}
              </Typography>
            )}
          </Box>
        </Stack>

        {/* Multiple Buttons Support */}
        {displayButtons.length > 0 && (
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.2}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            {displayButtons.map((btn, index) => {
              const BtnIcon = btn.icon;
              const variant = btn.variant || (index === 0 ? 'contained' : 'outlined');
              const color = btn.color || 'primary';

              return (
                <Button
                  key={index}
                  variant={variant}
                  color={color}
                  startIcon={BtnIcon ? <BtnIcon sx={{ fontSize: '1.2rem' }} /> : null}
                  onClick={btn.onClick}
                  disabled={btn.disabled}
                  sx={{
                    borderRadius: 2.4,
                    textTransform: 'none',
                    fontWeight: 700,
                    fontSize: '0.7rem',
                    px: 2.4,
                    py: 0.8,
                    ...(variant === 'contained' && {
                      background: `linear-gradient(135deg, ${
                        theme.palette[color]?.main || theme.palette.primary.main
                      } 0%, ${theme.palette[color]?.dark || theme.palette.primary.dark} 100%)`,
                      boxShadow: `0 3.2px 9.6px ${alpha(
                        theme.palette[color]?.main || theme.palette.primary.main,
                        0.4
                      )}`,
                      '&:hover': {
                        transform: 'translateY(-1.6px)',
                        boxShadow: `0 4.8px 16px ${alpha(
                          theme.palette[color]?.main || theme.palette.primary.main,
                          0.5
                        )}`,
                      },
                      '&.Mui-disabled': {
                        background: alpha(theme.palette.grey[400], 0.3),
                        color: alpha(theme.palette.text.disabled, 0.5),
                        boxShadow: 'none',
                      },
                    }),
                    ...(variant === 'outlined' && {
                      borderWidth: '1.2px',
                      '&:hover': {
                        borderWidth: '1.2px',
                        backgroundColor: alpha(
                          theme.palette[color]?.main || theme.palette.primary.main,
                          0.08
                        ),
                      },
                      '&.Mui-disabled': {
                        borderColor: alpha(theme.palette.grey[400], 0.3),
                        color: alpha(theme.palette.text.disabled, 0.5),
                      },
                    }),
                    transition: 'all 0.3s ease',
                    ...btn.sx,
                  }}
                >
                  {btn.text}
                </Button>
              );
            })}
          </Stack>
        )}
      </Stack>
    </Paper>
  );
};

/**
 * Compact (0.8x scaled) Modern Table component
 * All dimensions scaled down to 80% of original
 */
export function CompactModernTable({ title, ...props }) {
  const theme = useTheme();

  return (
    <Box
      sx={{
        width: '100%',
        borderRadius: 3.2,
        overflow: 'hidden',
        boxShadow: `0 6.4px 16px ${alpha(theme.palette.primary.main, 0.1)}`,
        border: `0.8px solid ${alpha(theme.palette.divider, 0.1)}`,

        // Header toolbar styles (0.8x)
        '& .MuiToolbar-root:not(.MuiTablePagination-toolbar)': {
          background: `linear-gradient(135deg, ${alpha(
            theme.palette.primary.main,
            0.08
          )} 0%, ${alpha(theme.palette.secondary.main, 0.08)} 100%) !important`,
          borderRadius: '3.2px 3.2px 0 0 !important',
          padding: '9.6px 25.6px !important',
          minHeight: 'auto !important',
        },

        // Title styles (0.8x)
        '& .MuiToolbar-root .MuiTypography-h6': {
          fontWeight: '700 !important',
          fontSize: '1rem !important',
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
        },

        // Table header row (0.8x)
        '& .MuiTableHead-root': {
          backgroundColor:
            theme.palette.mode === 'dark'
              ? alpha(theme.palette.background.paper, 0.6)
              : alpha(theme.palette.grey[50], 0.8),
        },
        '& .MuiTableCell-head': {
          fontWeight: '700 !important',
          fontSize: '0.7rem !important',
          textTransform: 'uppercase',
          letterSpacing: '0.4px',
          color: `${theme.palette.text.primary} !important`,
          borderBottom: `1.6px solid ${alpha(theme.palette.divider, 0.2)} !important`,
          padding: '8px 12px !important',
          whiteSpace: 'nowrap',
        },

        // Table body (0.8x)
        '& .MuiTableBody-root .MuiTableRow-root': {
          transition: 'background-color 0.2s ease !important',
          '&:hover': {
            backgroundColor: `${alpha(theme.palette.primary.main, 0.05)} !important`,
          },
          '&:nth-of-type(even)': {
            backgroundColor: `${alpha(theme.palette.action.hover, 0.02)} !important`,
          },
        },
        '& .MuiTableCell-body': {
          fontSize: '0.7rem !important',
          padding: '8px 12px !important',
          borderBottom: `0.8px solid ${alpha(theme.palette.divider, 0.08)} !important`,
        },
        // Detail panel arrow column - make it compact (only for main table, not nested tables)
        '& > table > thead > tr > th:first-of-type, & > table > tbody > tr > td:first-of-type': {
          width: '36px !important',
          minWidth: '36px !important',
          maxWidth: '36px !important',
          padding: '4px 8px !important',
        },

        // Pagination (0.8x)
        '& .MuiTablePagination-root': {
          backgroundColor:
            theme.palette.mode === 'dark'
              ? alpha(theme.palette.background.paper, 0.4)
              : alpha(theme.palette.grey[50], 0.5),
          borderTop: `0.8px solid ${alpha(theme.palette.divider, 0.1)}`,
        },
        '& .MuiTablePagination-toolbar': {
          padding: '12.8px 19.2px !important',
          minHeight: 'auto !important',
        },
        '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
          fontWeight: '500 !important',
          fontSize: '0.7rem !important',
          margin: '0 !important',
          marginBottom: '0 !important',
        },
        '& .MuiTablePagination-select': {
          paddingTop: '0 !important',
          paddingBottom: '0 !important',
        },

        // Search field styles (0.8x)
        '& .MuiToolbar-root .MuiTextField-root': {
          '& .MuiInputBase-root': {
            borderRadius: '20px !important',
            backgroundColor: `${alpha(theme.palette.background.paper, 0.8)} !important`,
            border: `1.6px solid ${alpha(theme.palette.primary.main, 0.2)} !important`,
            transition: 'all 0.3s ease !important',
            paddingLeft: '6.4px !important',
            '&:hover': {
              borderColor: `${alpha(theme.palette.primary.main, 0.5)} !important`,
              backgroundColor: `${theme.palette.background.paper} !important`,
              boxShadow: `0 3.2px 9.6px ${alpha(theme.palette.primary.main, 0.15)} !important`,
              transform: 'translateY(-0.8px) !important',
            },
            '&.Mui-focused': {
              borderColor: `${theme.palette.primary.main} !important`,
              backgroundColor: `${theme.palette.background.paper} !important`,
              boxShadow: `0 3.2px 16px ${alpha(theme.palette.primary.main, 0.25)} !important`,
              transform: 'translateY(-0.8px) !important',
            },
            '& fieldset': {
              border: 'none !important',
            },
          },
          '& .MuiInputBase-input': {
            padding: '8px 12.8px !important',
            fontSize: '0.7rem !important',
            fontWeight: '500 !important',
          },
          '& .MuiInputAdornment-root': {
            marginLeft: '6.4px !important',
          },
          '& .MuiInput-underline:before, & .MuiInput-underline:after, & .MuiInput-underline:hover:not(.Mui-disabled):before':
            {
              borderBottom: 'none !important',
            },
        },

        // Action buttons (0.8x)
        '& .MuiIconButton-root': {
          borderRadius: '6.4px !important',
          padding: '6.4px !important',
          transition: 'all 0.2s ease !important',
          '&:hover': {
            backgroundColor: `${alpha(theme.palette.primary.main, 0.1)} !important`,
            color: `${theme.palette.primary.main} !important`,
            transform: 'scale(1.1) !important',
          },
        },
      }}
    >
      <MaterialTable
        title={title}
        icons={tableIcons}
        {...props}
        options={{
          pageSize: 10,
          pageSizeOptions: [5, 10, 20, 50, 100],
          search: true,
          searchFieldAlignment: 'right',
          searchFieldStyle: {
            minWidth: '240px',
            borderRadius: '20px',
          },
          sorting: true,
          filtering: false,
          exportButton: true,
          exportAllData: true,
          actionsColumnIndex: -1,
          draggable: false,
          ...props.options,
        }}
        components={{
          Container: (props) => <Paper {...props} elevation={0} sx={{ boxShadow: 'none', overflow: 'hidden' }} />,
          ...props.components,
        }}
        localization={{
          pagination: {
            labelDisplayedRows: '{from}-{to} of {count}',
            labelRowsSelect: 'rows',
            firstAriaLabel: 'First Page',
            firstTooltip: 'First Page',
            previousAriaLabel: 'Previous Page',
            previousTooltip: 'Previous Page',
            nextAriaLabel: 'Next Page',
            nextTooltip: 'Next Page',
            lastAriaLabel: 'Last Page',
            lastTooltip: 'Last Page',
          },
          toolbar: {
            searchTooltip: 'Search',
            searchPlaceholder: 'Search...',
            exportTitle: 'Export',
            exportAriaLabel: 'Export',
            exportName: 'Export as CSV',
          },
          header: {
            actions: 'Actions',
          },
          body: {
            emptyDataSourceMessage: 'No records to display',
            filterRow: {
              filterTooltip: 'Filter',
            },
          },
          ...props.localization,
        }}
      />
    </Box>
  );
}

/**
 * Compact (0.8x scaled) Modern Page Header component
 */
export const CompactModernPageHeader = ({
  title,
  subtitle,
  icon: Icon,
  buttons = [],
  buttonText,
  onButtonClick,
  buttonIcon: ButtonIcon,
  secondaryButtonText,
  onSecondaryButtonClick,
  secondaryButtonIcon: SecondaryButtonIcon,
}) => {
  const theme = useTheme();

  // Convert legacy button props to buttons array
  const legacyButtons = [];
  if (buttonText && onButtonClick) {
    legacyButtons.push({
      text: buttonText,
      onClick: onButtonClick,
      icon: ButtonIcon,
      variant: 'contained',
    });
  }
  if (secondaryButtonText && onSecondaryButtonClick) {
    legacyButtons.push({
      text: secondaryButtonText,
      onClick: onSecondaryButtonClick,
      icon: SecondaryButtonIcon,
      variant: 'outlined',
    });
  }

  const displayButtons = buttons.length > 0 ? buttons : legacyButtons;

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2.4, sm: 3.2, md: 4 },
        mb: 3.2,
        borderRadius: 3.2,
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(
          theme.palette.secondary.main,
          0.08
        )} 100%)`,
        border: `0.8px solid ${alpha(theme.palette.divider, 0.1)}`,
        borderBottom: `2.4px solid ${theme.palette.primary.main}`,
      }}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        alignItems={{ xs: 'flex-start', md: 'center' }}
        justifyContent="space-between"
        spacing={2.4}
      >
        <Stack direction="row" alignItems="center" spacing={1.6}>
          {Icon && (
            <Box
              sx={{
                width: 38.4,
                height: 38.4,
                borderRadius: 2.4,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: `0 3.2px 16px ${alpha(theme.palette.primary.main, 0.3)}`,
              }}
            >
              <Icon sx={{ color: 'white', fontSize: 22.4 }} />
            </Box>
          )}
          <Box>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                fontSize: '1.6rem',
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: subtitle ? 0.4 : 0,
              }}
            >
              {title}
            </Typography>
            {subtitle && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontWeight: 500, fontSize: '0.7rem' }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
        </Stack>

        {displayButtons.length > 0 && (
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.2}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            {displayButtons.map((btn, index) => {
              const BtnIcon = btn.icon;
              const variant = btn.variant || (index === 0 ? 'contained' : 'outlined');
              const color = btn.color || 'primary';

              return (
                <Button
                  key={index}
                  variant={variant}
                  color={color}
                  startIcon={BtnIcon ? <BtnIcon sx={{ fontSize: '1.2rem' }} /> : null}
                  onClick={btn.onClick}
                  disabled={btn.disabled}
                  sx={{
                    borderRadius: 2.4,
                    textTransform: 'none',
                    fontWeight: 700,
                    fontSize: '0.7rem',
                    px: 2.4,
                    py: 0.8,
                    ...(variant === 'contained' && {
                      background: `linear-gradient(135deg, ${
                        theme.palette[color]?.main || theme.palette.primary.main
                      } 0%, ${theme.palette[color]?.dark || theme.palette.primary.dark} 100%)`,
                      boxShadow: `0 3.2px 9.6px ${alpha(
                        theme.palette[color]?.main || theme.palette.primary.main,
                        0.4
                      )}`,
                      '&:hover': {
                        transform: 'translateY(-1.6px)',
                        boxShadow: `0 4.8px 16px ${alpha(
                          theme.palette[color]?.main || theme.palette.primary.main,
                          0.5
                        )}`,
                      },
                      '&.Mui-disabled': {
                        background: alpha(theme.palette.grey[400], 0.3),
                        color: alpha(theme.palette.text.disabled, 0.5),
                        boxShadow: 'none',
                      },
                    }),
                    ...(variant === 'outlined' && {
                      borderWidth: '1.2px',
                      '&:hover': {
                        borderWidth: '1.2px',
                        backgroundColor: alpha(
                          theme.palette[color]?.main || theme.palette.primary.main,
                          0.08
                        ),
                      },
                      '&.Mui-disabled': {
                        borderColor: alpha(theme.palette.grey[400], 0.3),
                        color: alpha(theme.palette.text.disabled, 0.5),
                      },
                    }),
                    transition: 'all 0.3s ease',
                    ...btn.sx,
                  }}
                >
                  {btn.text}
                </Button>
              );
            })}
          </Stack>
        )}
      </Stack>
    </Paper>
  );
};
